import React from "react";
import moment from "moment";
import classNames from "classnames";
import TableRowCell from "./TableRowCell";
import TableRowActionColumn from "./TableRowActionColumn";
import TableRowPriorityColumn from "./TableRowPriorityColumn";
import useOpenMasterAccount from "../../../hooks/useOpenMasterAccount";
import { useAuth } from "react-oidc-context";
import { toNString } from "../../../helpers/guids";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateDisplay } from "../../../components";
import { ThemedSpan } from "../../../components/utilities";
import { GridMessageType } from "../../../types/MessageType";
import { TableLoadingRow } from "../../../components/tables/VirtualTable"

export type VirtualTableRowProps = {
    data: GridMessageType[]
    dateFormat?: string
    index: number
    style?: any
};

const PersonalMessagesVirtualTableRow = (
    { data, index, style, dateFormat = "DD/MM/YYYY" } : Readonly<VirtualTableRowProps>
) => {
    const { height } = style;
    const { user } = useAuth();
    const userId = toNString(user?.profile.sub);
    const openMasterAccount = useOpenMasterAccount();

    // do not return a data row when the data has not yet been collected from the backend
    if (index >= data.length) {
        return <TableLoadingRow style={{ height }} />
    }

    const {
        isActionRequired,
        masterAccountId,
        masterAccountName,
        messageId,
        message,
        subject,
        statusText,
        senderName,
        recipientNames,
        recipientIds,
        sentOnDate,
        isDone,
        messagePriority,
        userMessagePriority,
        deadlineDate,
        userReminderDate
    } = data[index];

    const onMasterAccountClick = (e: any) : void => {
        if (typeof(e?.preventDefault) === "function") {
            e.preventDefault();
        }
        openMasterAccount(masterAccountId);
    };

    return (
        <tr>
            <TableRowCell width={7.5} className="text-truncate" style={{ height }}>
                {moment(sentOnDate).isValid() ? <DateDisplay format="HH:mm DD/MM/YY">{sentOnDate}</DateDisplay> : "Draft"}
            </TableRowCell>
            <TableRowCell width={12.5} className="text-truncate">
                <ThemedSpan>{senderName} to {recipientNames.join(", ")}</ThemedSpan>
            </TableRowCell>
            <TableRowCell width={12.5} className="text-truncate">
                <ThemedSpan><a href="#" onClick={onMasterAccountClick}>{masterAccountName}</a></ThemedSpan>
            </TableRowCell>
            <TableRowCell width={12.5} className="text-truncate">
                <ThemedSpan>{subject}</ThemedSpan>
            </TableRowCell>
            <TableRowCell width={10} className="text-truncate">
                <ThemedSpan>{statusText}</ThemedSpan>
            </TableRowCell>
            <TableRowCell width={17.5} className="text-truncate">
                <ThemedSpan>{message}</ThemedSpan>
            </TableRowCell>
            <TableRowCell width={5}>
                <TableRowPriorityColumn value={messagePriority} />
            </TableRowCell>
            <TableRowCell width={6.5}>
                <TableRowPriorityColumn value={userMessagePriority} />
            </TableRowCell>
            <TableRowCell width={6}>
                {moment(deadlineDate).isValid() ? <DateDisplay>{deadlineDate}</DateDisplay> : <span className="py-1 px-2">-</span>}
            </TableRowCell>
            <TableRowCell width={6}>
                {moment(userReminderDate ?? null).isValid() ? <DateDisplay>{userReminderDate}</DateDisplay> : <span className="py-1 px-2">-</span>}
            </TableRowCell>
            <TableRowCell width={2.5} className="align-middle text-center action-col">
                {isActionRequired ? <FontAwesomeIcon
                    size="lg"
                    icon={['fas', isDone ? 'check' : 'xmark']}
                    className={classNames({
                        'text-success': isDone,
                        'text-danger': !isDone
                    })}
                /> : <span className="py-1 px-2">-</span>}
            </TableRowCell>
            <TableRowCell width={1.5} className="align-middle text-center action-col px-4">
                <TableRowActionColumn data={data[index]} userId={userId} isRecipient={data[index].recipientIds?.some(x => x === userId)} />
            </TableRowCell>
        </tr>
    );
};

export default PersonalMessagesVirtualTableRow;